"use client";

import React, { useEffect } from "react";
import { Clock, FileText, MapPin, PhoneCall, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function RequestService() {
  useEffect(() => {
    const linkId = "jobber-work-request-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      document.head.appendChild(link);
    }
    const scriptId = "jobber-work-request-script";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    document.body.appendChild(script);
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Technical Header & Dispatch Status */}
        <div className="mb-10 lg:mb-14 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-mono font-semibold uppercase tracking-wider mb-4">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            Active Regional Dispatch • MD / DC / NoVA
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground">
            Request mold inspection & remediation
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Submit your property details below for priority scheduling, or connect directly with our local restoration crews for immediate dispatch.
          </p>
        </div>

        {/* Main Grid: Form + 24/7 Callout Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Form Container (7 cols) */}
          <div className="lg:col-span-7">
            <Card className="border border-border shadow-md rounded-lg overflow-hidden bg-card">
              <CardHeader className="p-6 md:p-8 border-b border-border bg-muted/30">
                <CardTitle className="text-xl font-default font-bold text-foreground">
                  Direct intake form
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  All requests route straight to on-duty field supervisors across Maryland, DC, and Northern Virginia.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 md:p-8">
                <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
              </CardContent>
            </Card>
          </div>

          {/* 24/7 Callout Panel (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Primary Phone Dispatch Card */}
            <div className="rounded-lg border border-border bg-secondary text-secondary-foreground p-6 sm:p-8 shadow-lg">
              <div className="flex items-center justify-between pb-5 border-b border-secondary-foreground/15">
                <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-accent">
                  <span className="size-2 rounded-full bg-accent animate-ping" />
                  Live Emergency Line
                </div>
                <span className="text-xs bg-secondary-foreground/10 px-2.5 py-1 rounded text-secondary-foreground font-medium">
                  24/7 Standby
                </span>
              </div>

              <div className="py-6">
                <p className="text-sm font-medium text-secondary-foreground/80 mb-2">
                  Immediate property emergency? Speak with a field coordinator now:
                </p>
                <a href="tel:855-435-6631" className="inline-flex items-center gap-3 text-2xl sm:text-3xl font-mono font-bold text-secondary-foreground hover:text-accent transition-colors">
                  <PhoneCall className="size-7 text-accent shrink-0" />
                  <span>855-435-6631</span>
                </a>
                <p className="text-xs text-secondary-foreground/70 mt-2 font-mono">
                  Direct line • Zero automated menu delays
                </p>
              </div>

              <Button asChild className="w-full h-11 bg-accent text-accent-foreground font-bold hover:bg-accent/90 border-0">
                <a href="tel:855-435-6631">
                  <PhoneCall className="mr-2 size-4" />
                  Call 855-435-6631 now
                </a>
              </Button>
            </div>

            {/* Local Dispatch Times & Coverage Panel */}
            <div className="rounded-lg border border-border bg-card p-6 shadow-sm space-y-5">
              <h3 className="text-lg font-bold text-foreground font-default flex items-center gap-2">
                <Clock className="size-5 text-primary" />
                Local technician dispatch times
              </h3>

              <div className="space-y-3.5 text-sm">
                <div className="flex items-start justify-between pb-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-muted-foreground shrink-0" />
                    <span className="font-semibold text-foreground">Montgomery & Prince George's, MD</span>
                  </div>
                  <span className="font-mono font-bold text-primary text-xs bg-primary/10 px-2 py-0.5 rounded">
                    45–60 min
                  </span>
                </div>

                <div className="flex items-start justify-between pb-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-muted-foreground shrink-0" />
                    <span className="font-semibold text-foreground">Washington, DC (All Wards)</span>
                  </div>
                  <span className="font-mono font-bold text-primary text-xs bg-primary/10 px-2 py-0.5 rounded">
                    30–50 min
                  </span>
                </div>

                <div className="flex items-start justify-between pb-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-muted-foreground shrink-0" />
                    <span className="font-semibold text-foreground">Northern Virginia (Fairfax / Loudoun / Arlington)</span>
                  </div>
                  <span className="font-mono font-bold text-primary text-xs bg-primary/10 px-2 py-0.5 rounded">
                    45–60 min
                  </span>
                </div>
              </div>

              <div className="pt-2 space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/60 border border-border">
                  <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Direct insurance coordination</p>
                    <p className="text-xs text-muted-foreground">
                      We document moisture mapping and negative air containment to streamline claim approval with your adjuster.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/60 border border-border">
                  <FileText className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Certified clearance protocols</p>
                    <p className="text-xs text-muted-foreground">
                      Strict antimicrobial remediation adhering to IICRC S520 professional standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </section>;
}